import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { getReferralDashboard, getReferralDashboardByCode } from '../lib/api';
import { formatPrice } from '../lib/formatPrice';
import { usePaymentSettings } from '../lib/usePaymentSettings';
import { Loader2, Copy, Check, Users, Trophy, DollarSign, Share2, TrendingUp, XCircle } from 'lucide-react';
import type { ReferralDashboardData } from '../../../shared/types';

/**
 * Token-based referral/dealer dashboard (/referral/:token) — the magic link
 * delivered over WhatsApp. Customers see points + referred orders; dealers
 * additionally see commission and sales totals.
 */
const ReferralDashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const { token, code } = useParams<{ token?: string; code?: string }>();
  const [data, setData] = useState<ReferralDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [copied, setCopied] = useState(false);
  const { settings: paymentSettings } = usePaymentSettings();

  const priceOptions = {
    currencySymbol: paymentSettings.currencySymbol,
    locale: paymentSettings.priceLocale,
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // api.request throws on non-2xx (e.g. 404 for bad/expired links) —
      // catch and route to the invalid-link state instead of crashing
      try {
        let res;
        if (code) {
          res = await getReferralDashboardByCode(code);
        } else if (token) {
          res = await getReferralDashboard(token);
        } else {
          setNotFound(true);
          setLoading(false);
          return;
        }
        if (cancelled) return;
        if (res.success && res.data) setData(res.data);
        else setNotFound(true);
      } catch {
        if (!cancelled) setNotFound(true);
      }
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [token, code]);

  const shareText = useMemo(() => {
    if (!data) return '';
    return t('referral.shareText', {
      defaultValue: `我喺扶陽堂買咗好用嘅養生產品，用我嘅推薦碼 {{code}} 落單，每 1 港元仲可以幫我累積積分：{{url}}`,
      code: data.profile.referralCode,
      url: data.shareUrl,
    });
  }, [data, t]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(data?.shareUrl || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard unavailable — user can select manually
    }
  };

  const handleWhatsAppShare = () => {
    if (!shareText) return;
    window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-paper flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-brand-cinnabar" />
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div className="min-h-screen bg-brand-paper">
        <Header />
        <main className="pt-24 pb-20 px-4 text-center">
          <XCircle className="w-12 h-12 text-amber-600 mx-auto mb-4" />
          <h1 className="text-2xl font-serif text-brand-ink mb-2">{t('referral.invalidLink', '推薦連結無效')}</h1>
          <p className="text-brand-ink/60 mb-6">{t('referral.invalidLinkHint', '連結可能已重新產生，請重新取得最新的推薦連結。')}</p>
          <Link to="/" className="text-brand-cinnabar underline underline-offset-4">{t('common.backHome', '返回首頁')}</Link>
        </main>
        <Footer />
      </div>
    );
  }

  const { profile, records, ledger } = data;
  const isDealer = profile.role === 'dealer';

  return (
    <div className="min-h-screen bg-brand-paper">
      <Header />
      <main className="pt-24 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-serif tracking-widest text-brand-ink">
              {isDealer ? t('referral.dealerDashboard', '經銷商儀表板') : t('referral.myReferral', '我的推薦')}
            </h1>
            <p className="text-brand-ink/60 mt-2">
              {profile.name ? `${profile.name}・` : ''}
              <span className="font-mono">{profile.referralCode}</span>
            </p>
          </div>

          {/* Stat cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {!isDealer && (
              <div className="bg-white border border-brand-ink/10 p-5">
                <Trophy className="w-5 h-5 text-brand-cinnabar mb-2" />
                <p className="text-2xl font-medium text-brand-ink">{profile.totalPoints.toLocaleString()}</p>
                <p className="text-sm text-brand-ink/50">{t('referral.totalPoints', '總積分')}</p>
              </div>
            )}
            <div className="bg-white border border-brand-ink/10 p-5">
              <Users className="w-5 h-5 text-brand-cinnabar mb-2" />
              <p className="text-2xl font-medium text-brand-ink">{profile.referredCount}</p>
              <p className="text-sm text-brand-ink/50">{t('referral.referredOrders', '推薦訂單')}</p>
            </div>
            <div className="bg-white border border-brand-ink/10 p-5">
              <TrendingUp className="w-5 h-5 text-brand-cinnabar mb-2" />
              <p className="text-2xl font-medium text-brand-ink">{formatPrice(profile.totalSales, priceOptions)}</p>
              <p className="text-sm text-brand-ink/50">{t('referral.totalSales', '累計銷售')}</p>
            </div>
            {isDealer && (
              <div className="bg-white border border-brand-ink/10 p-5">
                <DollarSign className="w-5 h-5 text-brand-cinnabar mb-2" />
                <p className="text-2xl font-medium text-brand-ink">{formatPrice(profile.totalCommission, priceOptions)}</p>
                <p className="text-sm text-brand-ink/50">{t('referral.totalCommission', '累計佣金')}</p>
              </div>
            )}
          </div>

          {/* Share card (customers) */}
          {!isDealer && (
            <div className="bg-white border border-brand-ink/10 p-6 mb-8">
              <h2 className="text-lg font-serif text-brand-ink mb-3">{t('referral.shareTitle', '分享您的推薦連結')}</h2>
              <p className="text-sm text-brand-ink/60 mb-4">
                {t('referral.shareIntro', {
                  defaultValue: '親友以您的推薦碼下單，每 1 港元消費為您累積 {{ppd}} 積分。',
                  ppd: data.pointsPerDollar,
                })}
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1 flex items-center gap-3 border border-brand-ink/10 px-4 py-3 min-w-0">
                  <span className="font-mono text-sm text-brand-ink truncate">{data.shareUrl}</span>
                </div>
                <button
                  onClick={handleCopy}
                  className="inline-flex items-center justify-center gap-2 px-4 py-3 border border-brand-ink/20 hover:border-brand-cinnabar text-brand-ink transition-colors"
                >
                  {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                  {copied ? t('referral.copied', '已複製') : t('common.copy', '複製')}
                </button>
                <button
                  onClick={handleWhatsAppShare}
                  className="inline-flex items-center justify-center gap-2 px-4 py-3 bg-[#25D366] text-white hover:opacity-90 transition-opacity"
                >
                  <Share2 className="w-4 h-4" />
                  WhatsApp
                </button>
              </div>
            </div>
          )}

          {/* Records */}
          <div className="bg-white border border-brand-ink/10 p-6 mb-8">
            <h2 className="text-lg font-serif text-brand-ink mb-4">{t('referral.recordsTitle', '推薦記錄')}</h2>
            {records.length === 0 ? (
              <p className="text-sm text-brand-ink/50 py-4 text-center">{t('referral.noRecords', '暫無推薦記錄')}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-brand-ink/50 border-b border-brand-ink/10">
                      <th className="py-2 pr-4">{t('referral.orderNum', '訂單')}</th>
                      <th className="py-2 pr-4">{t('referral.customer', '客戶')}</th>
                      <th className="py-2 pr-4">{t('referral.amount', '金額')}</th>
                      {!isDealer && <th className="py-2 pr-4">{t('referral.points', '積分')}</th>}
                      {isDealer && <th className="py-2 pr-4">{t('referral.commission', '佣金')}</th>}
                      <th className="py-2">{t('referral.date', '日期')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {records.map((r) => (
                      <tr key={r.id} className={`border-b border-brand-ink/5 ${r.reversedAt ? 'opacity-50 line-through' : ''}`}>
                        <td className="py-2 pr-4 font-mono">{r.orderNum}</td>
                        <td className="py-2 pr-4">{r.customerName}</td>
                        <td className="py-2 pr-4">{formatPrice(r.salesAmount, priceOptions)}</td>
                        {!isDealer && <td className="py-2 pr-4 text-brand-cinnabar font-medium">+{r.pointsEarned}</td>}
                        {isDealer && <td className="py-2 pr-4 text-brand-cinnabar font-medium">{formatPrice(r.commissionEarned, priceOptions)}</td>}
                        <td className="py-2 text-brand-ink/60">{new Date(r.createdAt).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Points ledger (customers) */}
          {!isDealer && ledger.length > 0 && (
            <div className="bg-white border border-brand-ink/10 p-6">
              <h2 className="text-lg font-serif text-brand-ink mb-4">{t('referral.ledgerTitle', '積分明細')}</h2>
              <div className="space-y-2">
                {ledger.map((l) => (
                  <div key={l.id} className="flex items-center justify-between text-sm py-2 border-b border-brand-ink/5 last:border-0">
                    <div>
                      <p className="text-brand-ink">{l.description || t(`referral.ledgerType.${l.type}`, l.type)}</p>
                      <p className="text-xs text-brand-ink/40">{new Date(l.createdAt).toLocaleString()}</p>
                    </div>
                    <span className={l.amount >= 0 ? 'text-green-700 font-medium' : 'text-red-600 font-medium'}>
                      {l.amount >= 0 ? '+' : ''}{l.amount}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ReferralDashboardPage;
